import React, { useEffect } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useFinancial } from '../../../context/FinancialContext';
import { fmt, getRetireSpending } from './walkthroughUtils';

const { FiCheck, FiActivity, FiHome, FiCoffee, FiCompass } = FiIcons;
const OPTIONS = [
  { mult: 25, label: 'Standard (25x)', note: '4% starting withdrawal' },
  { mult: 28, label: 'Conservative (28x)', note: '≈3.6% starting withdrawal' }
];

export const FiNumberTool = ({ shared, patch }) => {
  const { state, dispatch } = useFinancial();
  const spending = getRetireSpending(shared, state.calculations.monthlyExpenses);
  const target = spending * 12 * shared.multiplier;
  useEffect(() => {
    if (target > 0 && state.goals.targetAmount !== target) {
      dispatch({ type: 'UPDATE_GOALS', payload: { targetAmount: target } });
    }
  }, [target, state.goals.targetAmount, dispatch]);
  return (
    <div className="space-y-4">
      <div>
        <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Retirement spending (per month)</label>
        <div className="relative mt-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-black text-xs">$</span>
          <input type="number" min="0" value={spending} onChange={event => patch({ retireSpending: parseFloat(event.target.value) || 0 })} className="w-full pl-7 pr-4 py-3 bg-slate-50 rounded-xl font-black text-sm text-slate-900 outline-none focus:ring-2 focus:ring-purple-500" />
        </div>
        <p className="text-[9px] font-bold text-slate-400 mt-1">Monthly spending × 12 × your chosen multiplier = FI target.</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {OPTIONS.map(option => {
          const active = shared.multiplier === option.mult;
          return (
            <button key={option.mult} onClick={() => patch({ multiplier: option.mult })} className={`text-left p-3 rounded-xl ${active ? 'bg-purple-600 text-white shadow-lg' : 'bg-slate-50 text-slate-600 hover:bg-purple-50'}`}>
              <div className="flex justify-between items-center"><p className="text-[8px] font-black uppercase">{option.label}</p>{active && <SafeIcon icon={FiCheck} />}</div>
              <p className="text-xs font-black mt-1">{fmt(spending * 12 * option.mult)}</p>
              <p className="text-[8px] font-bold mt-1 opacity-75">{option.note}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const FiNumberVisual = ({ shared }) => {
  const { state } = useFinancial();
  const spending = getRetireSpending(shared, state.calculations.monthlyExpenses);
  const annual = spending * 12;
  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 p-6 text-white shadow-lg">
        <div className="absolute right-[-20px] top-[-20px] w-32 h-32 rounded-full border-[18px] border-white/10" />
        <p className="text-[9px] font-black uppercase tracking-widest text-blue-100">Your financial freedom number</p>
        <p className="text-3xl font-black tracking-tight mt-3">{fmt(annual * shared.multiplier)}</p>
        <p className="text-[10px] font-medium text-blue-100 mt-2">A planning target for the life you want to fund.</p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[{ icon: FiHome, title: 'Your home', text: 'Cover essentials' }, { icon: FiCoffee, title: 'Your time', text: 'Choose your days' }, { icon: FiCompass, title: 'Your freedom', text: 'Make room to explore' }].map(item => (
          <div key={item.title} className="p-3 bg-white rounded-2xl border border-slate-100 shadow-sm">
            <SafeIcon icon={item.icon} className="text-blue-600 mb-2 text-lg" />
            <p className="text-[10px] font-black text-slate-900">{item.title}</p>
            <p className="text-[9px] text-slate-500">{item.text}</p>
          </div>
        ))}
      </div>
      <div className="p-4 bg-white border border-slate-100 rounded-2xl text-[10px] font-semibold text-slate-600">
        {fmt(spending)} per month → {fmt(annual)} per year → {fmt(annual * shared.multiplier)} at {shared.multiplier}×.
      </div>
      <p className="text-[10px] font-bold text-slate-400">Completing this step reveals the dotted target on Wealth Trajectory. This is a planning illustration, not guaranteed investment income.</p>
    </div>
  );
};

const GaugeDial = ({ current, required }) => {
  const percentage = required > 0 ? Math.min(100, Math.max(0, current / required * 100)) : 100;
  const circumference = Math.PI * 80;
  const rotation = -90 + percentage / 100 * 180;
  return (
    <div className="flex flex-col items-center">
      <svg className="w-full max-w-[280px] overflow-visible" viewBox="0 0 220 110">
        <path d="M 30 85 A 80 80 0 0 1 190 85" fill="none" stroke="#e2e8f0" strokeWidth="14" strokeLinecap="round" />
        <path d="M 30 85 A 80 80 0 0 1 190 85" fill="none" stroke={current >= required ? '#10b981' : '#f97316'} strokeWidth="14" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference * (1 - percentage / 100)} />
        <g transform={`translate(110,85) rotate(${rotation})`}><polygon points="-2.5,0 2.5,0 0,-60" fill="#64748b" /><circle cx="0" cy="0" r="6" fill="#64748b" /></g>
        <text x="30" y="105" textAnchor="middle" fill="#94a3b8" fontSize="9">$0</text>
        <text x="190" y="105" textAnchor="middle" fill="#94a3b8" fontSize="9">{fmt(required)}</text>
      </svg>
      <p className="text-3xl font-black text-slate-900 mt-4">{fmt(current)}</p>
      <p className="text-[9px] font-bold text-slate-400 uppercase">Monthly invested</p>
    </div>
  );
};

export const AlignTool = () => {
  const { state } = useFinancial();
  const { goals, calculations } = state;
  const gap = calculations.requiredMonthlyInvestment - calculations.monthlyInvested;
  const cards = [
    ['Invested Capital', fmt(goals.startingNetWorth)],
    ['Monthly Invested', fmt(calculations.monthlyInvested)],
    ['Required Monthly', fmt(calculations.requiredMonthlyInvestment)],
    [gap > 0 ? 'Monthly Gap' : 'Monthly Surplus', fmt(Math.abs(gap))]
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {cards.map(([label, value]) => <div key={label} className="p-3 bg-slate-50 rounded-xl border border-slate-100"><p className="text-[8px] font-black text-slate-400 uppercase">{label}</p><p className="text-sm font-black text-slate-900">{value}</p></div>)}
      </div>
      <div className="p-3 bg-orange-50 rounded-xl border border-orange-100 flex gap-2"><SafeIcon icon={FiActivity} className="text-orange-500 flex-shrink-0" /><p className="text-[10px] font-bold text-orange-800">{gap > 0 ? `${fmt(gap)} more per month is needed for the FI target on this timeline.` : 'Your monthly investing meets or exceeds the calculated contribution.'} Revisit this as circumstances change.</p></div>
    </div>
  );
};

export const AlignVisual = () => {
  const { state } = useFinancial();
  return <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm"><GaugeDial current={state.calculations.monthlyInvested} required={state.calculations.requiredMonthlyInvestment} /></div>;
};